import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AlertTriangle, MapPin, Clock, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";

// Fire Meta Pixel Purchase event once per booking. Uses localStorage as a
// durable guard so page refreshes, new browser sessions, or shared/re-opened
// receipt links never double-count the conversion. Always sends an eventID
// (session_id when present, booking id as fallback) so once we wire the
// Conversions API server-side, client + server events will deduplicate.
function trackPurchaseOnce(bookingId, valueEur, sessionId) {
  if (!bookingId || !valueEur) return;
  const key = `fbq_purchase_${bookingId}`;
  try {
    if (typeof window === "undefined") return;
    const store = window.localStorage || window.sessionStorage;
    if (store && store.getItem(key)) return;
    if (typeof window.fbq !== "function") return;
    const eventID = sessionId || bookingId;
    window.fbq(
      "track",
      "Purchase",
      { value: Number(valueEur), currency: "EUR" },
      { eventID }
    );
    if (store) store.setItem(key, "1");
  } catch (_) {
    /* pixel best-effort */
  }
}

/**
 * Public booking receipt page — the "View your booking" link in the
 * confirmation email lands here, and Stripe's success URL also redirects
 * here with `?session_id=…`. When a session id is present we poll
 * `/api/payments/status/:session_id` until Stripe reports `paid`, then
 * refresh the booking to reveal the composite receipt image + email trigger.
 *
 * The booking UUID is treated as a capability token — anyone with the
 * link can view (but not modify) the booking.
 */
export default function BookingDetailsPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payState, setPayState] = useState(sessionId ? "polling" : "idle");
  // "idle" | "polling" | "paid" | "expired" | "failed"

  // Fire Meta Pixel Purchase event once when we've confirmed the booking is paid.
  useEffect(() => {
    if (!booking) return;
    const isPaid =
      booking.payment_status === "paid" || booking.status === "confirmed";
    if (!isPaid) return;
    trackPurchaseOnce(booking.id, booking.total_price_eur, sessionId);
  }, [booking, sessionId]);

  const loadBooking = async () => {
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setBooking(data);
      return data;
    } catch (err) {
      setError(formatApiError(err));
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBooking();
  }, [id]);

  // Poll Stripe status when we arrive from a Checkout redirect.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 12; // 12 × 2s = 24s
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPayState("paid");
          // Give the backend a beat to persist the composite image, then
          // refresh the booking to show it.
          setTimeout(async () => {
            await loadBooking();
            searchParams.delete("session_id");
            setSearchParams(searchParams, { replace: true });
          }, 900);
          return;
        }
        if (data.status === "expired") {
          setPayState("expired");
          return;
        }
        if (attempts >= maxAttempts) {
          setPayState("failed");
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        if (attempts >= maxAttempts) {
          setPayState("failed");
          return;
        }
        setTimeout(tick, 2500);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white">
        <span className="font-heading text-3xl tracking-[0.35em] text-[#FF00A6]">LOADING…</span>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#050505] text-white p-6">
        <AlertTriangle className="w-14 h-14 text-[#FF00A6] mb-4" />
        <h1 className="font-heading text-3xl tracking-widest mb-2">BOOKING NOT FOUND</h1>
        <p className="text-white/60 text-sm mb-6 text-center max-w-md">
          {error || "This booking link is invalid or has expired."}
        </p>
        <Link
          to="/"
          className="px-6 py-3 border-2 border-[#FF00A6] rounded-full uppercase tracking-[0.25em] text-xs font-bold hover:bg-[#FF00A6]/10 transition"
          data-testid="booking-not-found-home-link"
        >
          Back to Soho69
        </Link>
      </div>
    );
  }

  const isTable = booking.type === "table";
  const label =
    booking.table_name || (isTable ? "VIP Table" : "VIP Pre-Order");
  const bottles = parseBottleNotes(booking.notes);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="booking-details-page">
      {/* Radial neon backdrop */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 20% 20%, rgba(255,0,166,0.18) 0%, transparent 55%), radial-gradient(ellipse at 85% 80%, rgba(120,20,140,0.16) 0%, transparent 55%)",
        }}
      />

      <div className="relative max-w-2xl mx-auto px-4 py-8 sm:py-12">
        {/* SOHO69 wordmark */}
        <div className="text-center mb-6">
          <Link
            to="/"
            className="inline-block font-heading text-4xl sm:text-5xl italic text-[#FF00A6] tracking-[0.02em]"
            style={{
              textShadow:
                "0 0 20px rgba(255,0,166,0.75), 0 0 40px rgba(255,0,166,0.4)",
              transform: "skew(-8deg)",
            }}
            data-testid="booking-details-brand"
          >
            SOHO69
          </Link>
        </div>

        {/* Payment progress banner (only during / just after Stripe redirect) */}
        {payState === "polling" ? (
          <div
            className="mb-6 rounded-2xl border border-[#FF00A6]/60 bg-[#FF00A6]/10 backdrop-blur px-5 py-4 flex items-center gap-3"
            data-testid="payment-polling-banner"
          >
            <Loader2 className="w-5 h-5 text-[#FF00A6] animate-spin" />
            <div className="text-sm text-white">
              <span className="font-bold">Confirming your payment…</span>
              <div className="text-white/70 text-xs mt-0.5">
                Please don&apos;t close this page — Stripe is finalising your payment.
              </div>
            </div>
          </div>
        ) : null}
        {payState === "paid" ? (
          <div
            className="mb-6 rounded-2xl border border-emerald-400/60 bg-emerald-400/10 backdrop-blur px-5 py-4 flex items-center gap-3"
            data-testid="payment-success-banner"
          >
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <div className="text-sm text-white">
              <span className="font-bold">Payment confirmed — see you at Soho69.</span>
              <div className="text-white/70 text-xs mt-0.5">
                Your deposit has been received. Remaining balance is due at the venue.
              </div>
            </div>
          </div>
        ) : null}
        {payState === "expired" || payState === "failed" ? (
          <div
            className="mb-6 rounded-2xl border border-amber-400/60 bg-amber-400/10 backdrop-blur px-5 py-4"
            data-testid="payment-warning-banner"
          >
            <div className="text-sm text-white font-bold">Payment not confirmed yet.</div>
            <div className="text-white/70 text-xs mt-1">
              If you completed payment on Stripe just now, please refresh this page in a moment.
              Otherwise, restart your booking from the{" "}
              <Link to="/book" className="text-[#FF00A6] underline">
                booking page
              </Link>
              .
            </div>
          </div>
        ) : null}

        {/* Composite receipt image (dynamic PNG served from /api/files/:id) */}
        {booking.receipt_image_url ? (
          <img loading="lazy" decoding="async"             src={booking.receipt_image_url}
            alt="Your Soho69 booking receipt"
            className="w-full rounded-2xl border border-[#FF00A6]/40 shadow-[0_0_40px_rgba(255,0,166,0.25)] mb-8"
            data-testid="booking-receipt-image"
          />
        ) : null}

        {/* Status pill */}
        <div className="flex justify-center mb-8">
          <span
            className="px-4 py-1.5 rounded-full text-[10px] tracking-[0.3em] uppercase font-bold border"
            style={
              booking.payment_status === "paid" || booking.status === "confirmed"
                ? {
                    color: "#4ade80",
                    borderColor: "rgba(74,222,128,0.5)",
                    background: "rgba(74,222,128,0.08)",
                  }
                : booking.status === "cancelled"
                ? {
                    color: "#f87171",
                    borderColor: "rgba(248,113,113,0.5)",
                    background: "rgba(248,113,113,0.08)",
                  }
                : {
                    color: "#FFC857",
                    borderColor: "rgba(255,200,87,0.5)",
                    background: "rgba(255,200,87,0.08)",
                  }
            }
            data-testid="booking-status-pill"
          >
            {booking.payment_status === "paid" || booking.status === "confirmed"
              ? (Number(booking.balance_eur ?? 0) > 0
                  ? "Deposit Paid · Confirmed"
                  : "Paid in Full · Confirmed")
              : booking.status === "cancelled"
              ? "Cancelled"
              : "Awaiting Payment"}
          </span>
        </div>

        {/* Guest + booking meta card */}
        <div className="rounded-2xl border border-[#FF00A6]/40 bg-black/60 backdrop-blur p-6 sm:p-8 mb-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">
            Booking for
          </div>
          <div
            className="font-heading text-3xl sm:text-4xl tracking-wide text-white mb-1"
            data-testid="booking-guest-name"
          >
            {booking.name}
          </div>
          <div className="text-[#FF00A6] text-lg font-semibold" data-testid="booking-label">
            {label}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4 sm:gap-6">
            {booking.date ? (
              <MetaCell label="Date" value={booking.date} testid="booking-date" />
            ) : null}
            {booking.arrival_time ? (
              <MetaCell
                label="Arrival"
                value={booking.arrival_time}
                testid="booking-arrival"
              />
            ) : null}
            {booking.party_size ? (
              <MetaCell
                label="Party"
                value={`${booking.party_size} ${
                  booking.party_size === 1 ? "guest" : "guests"
                }`}
                testid="booking-party-size"
              />
            ) : null}
            <MetaCell
              label="Booking ID"
              value={`#${(booking.id || "").slice(0, 8).toUpperCase()}`}
              testid="booking-id"
            />
          </div>
        </div>

        {/* Bottle order */}
        {bottles.length > 0 ? (
          <div
            className="rounded-2xl border border-[#FF00A6]/40 bg-black/60 backdrop-blur p-6 sm:p-8 mb-6"
            data-testid="booking-bottles-card"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF00A6] font-bold mb-4">
              {isTable ? "Bottle Pre-Order" : "Your Bottle Order"}
            </div>
            <ul className="space-y-3">
              {bottles.map((b, i) => (
                <li
                  key={`${b.name}-${b.qty}-${i}`}
                  className="flex items-center justify-between text-sm sm:text-base border-b border-white/5 pb-3 last:border-0 last:pb-0"
                  data-testid={`booking-bottle-row-${i}`}
                >
                  <span className="flex items-center gap-3">
                    <span className="text-[#FF00A6] font-bold w-6 text-right">
                      {b.qty}×
                    </span>
                    <span className="text-white/90">{b.name}</span>
                  </span>
                  {b.price ? (
                    <span className="text-white/70 font-mono">€{b.price}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : booking.notes ? (
          <div
            className="rounded-2xl border border-[#FF00A6]/40 bg-black/60 backdrop-blur p-6 sm:p-8 mb-6 text-sm text-white/80 leading-relaxed"
            data-testid="booking-notes-card"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF00A6] font-bold mb-3">
              Notes
            </div>
            {booking.notes}
          </div>
        ) : null}

        {/* Receipt breakdown */}
        <div
          className="rounded-2xl border-2 border-[#FF00A6] bg-black/60 backdrop-blur p-6 sm:p-8 mb-6"
          style={{ boxShadow: "0 0 30px rgba(255,0,166,0.25)" }}
          data-testid="booking-receipt-breakdown"
        >
          <ReceiptRow
            label="Total"
            value={`€${booking.total_price_eur}`}
            variant="white"
            testid="receipt-total"
          />
          {booking.payment_status === "paid" ? (
            Number(booking.balance_eur ?? 0) > 0 ? (
              <>
                <div className="border-t border-dashed border-[#FF00A6]/50 my-4" />
                <ReceiptRow
                  label="30% Deposit Paid"
                  value={`€${booking.deposit_eur}`}
                  variant="pink"
                  testid="receipt-deposit"
                />
                <div className="border-t border-dashed border-[#FF00A6]/50 my-4" />
                <ReceiptRow
                  label="Balance Due at Club"
                  value={`€${booking.balance_eur}`}
                  variant="white"
                  testid="receipt-balance"
                />
                <div className="mt-5 text-center text-[11px] text-white/50 leading-relaxed">
                  <span className="text-[#FF00A6] font-bold">
                    Deposit received. Remaining €{booking.balance_eur} due at
                    the club on arrival.
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="border-t border-dashed border-[#FF00A6]/50 my-4" />
                <ReceiptRow
                  label="Paid in Full"
                  value="€0 due at venue"
                  variant="pink"
                  testid="receipt-deposit"
                />
                <div className="mt-5 text-center text-[11px] text-white/50 leading-relaxed">
                  <span className="text-[#FF00A6] font-bold">
                    Payment received in full. Zero balance due at the venue.
                  </span>
                </div>
              </>
            )
          ) : (
            <>
              <div className="border-t border-dashed border-[#FF00A6]/50 my-4" />
              <ReceiptRow
                label="Awaiting Payment"
                value={`€${booking.total_price_eur}`}
                variant="pink"
                testid="receipt-deposit"
              />
              <div className="mt-5 text-center text-[11px] text-white/50 leading-relaxed">
                <span className="text-[#FF00A6]">
                  Awaiting payment — complete online to confirm.
                </span>
              </div>
            </>
          )}
        </div>

        {/* Venue footer */}
        <div className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur p-6 grid grid-cols-3 gap-3 text-[10px] tracking-[0.2em] uppercase text-white/60 mb-6">
          <div className="flex flex-col items-start gap-2">
            <MapPin className="w-4 h-4 text-[#FF00A6]" />
            Playa de las
            <br />
            Américas
          </div>
          <div className="flex flex-col items-center gap-2">
            <MessageCircle className="w-4 h-4 text-[#FF00A6]" />
            <a
              href="https://wa.me/34611379736"
              className="text-white/80 normal-case tracking-normal text-xs"
              data-testid="booking-whatsapp-link"
            >
              +34 611 37 97 36
            </a>
          </div>
          <div className="flex flex-col items-end gap-2 text-right">
            <Clock className="w-4 h-4 text-[#FF00A6]" />
            Mon – Sun
            <br />
            11:00 – 04:00
          </div>
        </div>

        <div className="text-center">
          <Link
            to="/"
            className="inline-block text-[10px] tracking-[0.3em] uppercase text-white/40 hover:text-[#FF00A6] transition"
            data-testid="booking-back-to-home"
          >
            ← Back to Soho69
          </Link>
        </div>
      </div>
    </div>
  );
}

function MetaCell({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-white/40 mb-1">
        {label}
      </div>
      <div className="text-sm sm:text-base text-white font-semibold" data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

function ReceiptRow({ label, value, variant, testid }) {
  const labelClass =
    variant === "white"
      ? "text-white/80"
      : "text-[#FF00A6] font-bold";
  const valueClass =
    variant === "white"
      ? "text-white text-xl sm:text-2xl font-bold"
      : variant === "pink-lg"
      ? "text-[#FF00A6] text-3xl sm:text-4xl font-bold"
      : "text-[#FF00A6] text-xl sm:text-2xl font-bold";

  return (
    <div className="flex items-start justify-between gap-4">
      <div
        className={`text-[10px] sm:text-[11px] tracking-[0.25em] uppercase leading-tight ${labelClass}`}
      >
        {label}
      </div>
      <div className={`font-mono ${valueClass}`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

/**
 * Parse the auto-generated "Bottle preferences: 1× Grey Goose 700ml (€300), 2× ..."
 * string from `formatBottleNotes()` into structured rows for display.
 * If the notes don't match the expected shape, returns [].
 */
function parseBottleNotes(notes) {
  if (!notes) return [];
  const prefix = "Bottle preferences:";
  if (!notes.includes(prefix)) return [];
  // The bottle list is ALWAYS the first physical line after the prefix.
  // Any guest free-text note that follows on the next line must not be
  // glued onto the last bottle name via the comma split below.
  const afterPrefix = notes.split(prefix)[1] || "";
  const list = afterPrefix.split("\n")[0] || "";
  return list
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((entry) => {
      // e.g. "1× Grey Goose 700ml (€300)"
      const qtyMatch = entry.match(/^(\d+)[×x]\s*/);
      const priceMatch = entry.match(/\(€(\d+(?:\.\d+)?)\)\s*$/);
      const qty = qtyMatch ? Number(qtyMatch[1]) : 1;
      const price = priceMatch ? Number(priceMatch[1]) : 0;
      const name = entry
        .replace(/^(\d+)[×x]\s*/, "")
        .replace(/\(€\d+(?:\.\d+)?\)\s*$/, "")
        .trim();
      return { qty, name, price };
    })
    .filter((b) => b.name);
}
